import { useCallback, useMemo, useState } from 'react';

import { useStatsigClient } from '@statsig/react-bindings';

import { REPORTS_TABS, TAB_INDICES } from '../../../constants/reports';
import { CAN_SEE_HEALTH_REPORTS_FG, CAN_SEE_PAYROLL_REPORTS_FG } from '../../../constants/statsig';
import { CLICK_ACTION, REPORTS_TABS_TAG } from '../../../constants/tagManager';
import TagManagerService from '../../../services/gtm/tagManagerService';

export const useTabs = (isRFPlan: boolean, isHealthPlan: boolean) => {
  const { client } = useStatsigClient();
  const [tabIndex, setTabIndex] = useState<number>(0);

  const tabsItems = useMemo(() => {
    const items = [...REPORTS_TABS];

    if (!isRFPlan) {
      items.splice(TAB_INDICES.PAYROLL, 1);
    }
    if (!isHealthPlan) {
      const healthIndex = items.findIndex((tab) => tab.id === TAB_INDICES.HEALTH);
      items.splice(healthIndex, 1);
    }
    return items;
  }, [isRFPlan, isHealthPlan]);

  const hasRfReportAccess = useMemo(() => client?.checkGate(CAN_SEE_PAYROLL_REPORTS_FG), [client]);
  const hasHealthAccess = useMemo(() => client?.checkGate(CAN_SEE_HEALTH_REPORTS_FG), [client]);

  const sendGtmEvent = useCallback((tab: string) => {
    TagManagerService.pushEvent(CLICK_ACTION, REPORTS_TABS_TAG, { section: tab });
  }, []);

  const onTabChange = useCallback(
    (index: number) => {
      const currentId = tabsItems[index].id;
      const eventMap = {
        [TAB_INDICES.PAYROLL]: 'PayrollReport',
        [TAB_INDICES.MONTHLY]: 'MonthlyReport',
        [TAB_INDICES.HEALTH]: 'HealthReport'
      };

      const eventName = eventMap[currentId as keyof typeof eventMap];
      if (eventName) {
        sendGtmEvent(eventName);
      }

      setTabIndex(index);
    },
    [sendGtmEvent, tabsItems]
  );

  const shouldShowTabs = useMemo(
    () => (isRFPlan && hasRfReportAccess) || (isHealthPlan && hasHealthAccess),
    [isRFPlan, hasRfReportAccess, hasHealthAccess, isHealthPlan]
  );

  const showPayrollReports = useMemo(
    () => isRFPlan && tabsItems[tabIndex]?.id === TAB_INDICES.PAYROLL && hasRfReportAccess,
    [isRFPlan, tabIndex, hasRfReportAccess, tabsItems]
  );

  const showHealthReports = useMemo(
    () => isHealthPlan && tabsItems[tabIndex]?.id === TAB_INDICES.HEALTH && hasHealthAccess,
    [isHealthPlan, tabIndex, hasHealthAccess, tabsItems]
  );

  return {
    tabIndex,
    tabsItems,
    onTabChange,
    shouldShowTabs,
    showPayrollReports,
    showHealthReports
  };
};
